import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Form, Input, Message, Select, Spin, Typography } from '@arco-design/web-react';
import axios, { AxiosError } from 'axios';

import { endpoints } from '../../api/endpoints';
import { readGuestIntakeAttribution, GuestConvertRole } from '../../constants/guestIntake';
import { QR_PROFILE_SOURCE } from '../../constants/qrProfile';
import { QR_REGISTRATION_SOURCE } from '../../constants/qrRegistration';
import { Logo } from '../../components/Logo';
import { LanguageSelect } from '../../components/LanguageSelect/LanguageSelect';
import { routes } from '../../routes/routes';
import { RootState } from '../../store/store';
import { setUser } from '../../store/userSlice';
import { getAuthLogoType } from '../../theme';
import { getBoxBackgroundColor, getButtonColor, getButtonTextColor, getTextColor } from '../../utils/cssVariables';
import { completeAuthRedirect } from '../../utils/authReturnUrl';
import { ownProfilePathAfterUpdate } from '../../utils/ownProfilePath';
import { parsePublicProfileRouteId } from '../../utils/publicProfilePath';

import { GuestRoleChoicePanel } from './GuestRoleChoicePanel';
import './GuestIntakeForm.css';

const FormItem = Form.Item;
const GUEST_ROLE_CHOICE_ID = 'guest-role-choice';

const requiredTrimRule = (message: string) => ({
  required: true,
  message,
  validator: (value: unknown, callback: (error?: string) => void) => {
    if (!value || String(value).trim().length === 0) {
      callback(message);
      return;
    }
    callback();
  },
});

type GuestIntakeFormProps = {
  highlightRoleChoice?: boolean;
};

export const GuestIntakeForm = ({ highlightRoleChoice = false }: GuestIntakeFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const user = useSelector((state: RootState) => state.user.user);
  const [submitting, setSubmitting] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [convertingRole, setConvertingRole] = useState<GuestConvertRole | null>(null);
  const roleChoiceRef = useRef<HTMLDivElement>(null);
  const intakeFormRef = useRef<HTMLDivElement>(null);

  const isEditing = Boolean(user?.guestIntakeCompleted);
  const attribution = useMemo(() => readGuestIntakeAttribution(searchParams), [searchParams]);
  const scannedProfileId = useMemo(
    () => parsePublicProfileRouteId(attribution.profileId ?? undefined),
    [attribution.profileId],
  );

  const scrollToRoleChoice = useCallback(() => {
    roleChoiceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    form.setFieldsValue({
      name: user.name ?? '',
      last_name: user.last_name ?? '',
      language: user.language ?? 'en',
    });
  }, [form, user]);

  useEffect(() => {
    if (!user?.guestIntakeCompleted) {
      return;
    }

    let cancelled = false;
    setLoadingProfile(true);

    axios
      .get(endpoints.currentProfile)
      .then((response) => {
        if (cancelled) {
          return;
        }
        form.setFieldsValue({
          name: response.data.name ?? user.name ?? '',
          last_name: response.data.last_name ?? user.last_name ?? '',
          shortDescription: response.data.shortDescription ?? '',
          fullBio: response.data.fullBio ?? '',
          language: response.data.language ?? user.language ?? 'en',
        });
      })
      .catch(() => {
        if (!cancelled) {
          Message.error(t('notification_messages.unexpected_error'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingProfile(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [form, t, user]);

  useEffect(() => {
    if (!highlightRoleChoice) {
      return;
    }
    scrollToRoleChoice();
  }, [highlightRoleChoice, scrollToRoleChoice]);

  const onSubmit = async () => {
    try {
      const values = await form.validate();
      setSubmitting(true);

      const payload: Record<string, unknown> = {
        name: values.name,
        last_name: values.last_name,
        shortDescription: values.shortDescription,
        fullBio: values.fullBio,
        language: values.language,
      };

      if (attribution.source === QR_REGISTRATION_SOURCE) {
        payload.source = QR_REGISTRATION_SOURCE;
      } else if (attribution.source === QR_PROFILE_SOURCE || scannedProfileId) {
        payload.source = QR_PROFILE_SOURCE;
        if (scannedProfileId) {
          payload.scanned_profile_id = Number(scannedProfileId);
        }
      }

      const response = await axios.post(endpoints.guestIntake, payload);
      dispatch(setUser({ ...user!, ...response.data }));
      Message.success({ content: t('guest_intake.success_message'), closable: true });

      if (!isEditing) {
        completeAuthRedirect(routes.DASHBOARD);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const message = (error.response?.data as { error?: string } | undefined)?.error;
        if (message === 'event intake disabled') {
          Message.error(t('notification_messages.event_intake_disabled'));
          return;
        }
        if (message === 'guest window expired') {
          Message.error(t('notification_messages.guest_window_expired'));
          return;
        }
      }
      Message.error(t('notification_messages.unexpected_error'));
    } finally {
      setSubmitting(false);
    }
  };

  const convertRole = async (role: GuestConvertRole) => {
    if (!user || convertingRole) {
      return;
    }

    if (!user.guestIntakeCompleted) {
      Message.warning(t('guest_conversion.intake_required'));
      intakeFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    setConvertingRole(role);
    try {
      const response = await axios.post(endpoints.guestConvert, { role });
      const convertedUser = { ...user, ...response.data };
      dispatch(setUser(convertedUser));
      Message.success({
        content: t('guest_conversion.success_message', {
          role: t(role === 'mentor' ? 'common.mentor' : 'common.mentee'),
        }),
        closable: true,
      });
      const destination = ownProfilePathAfterUpdate(convertedUser, convertedUser.id);
      navigate(destination, { replace: true });
    } catch (error) {
      if (error instanceof AxiosError) {
        const message = (error.response?.data as { error?: string } | undefined)?.error;
        if (message === 'event intake disabled') {
          Message.error(t('notification_messages.event_intake_disabled'));
          return;
        }
        if (message === 'guest window expired') {
          Message.error(t('notification_messages.guest_window_expired'));
          return;
        }
        if (message === 'complete guest intake before converting') {
          Message.error(t('guest_conversion.intake_required'));
          return;
        }
      }
      Message.error(t('notification_messages.unexpected_error'));
    } finally {
      setConvertingRole(null);
    }
  };

  if (loadingProfile) {
    return <Spin style={{ margin: '120px auto' }} size={32} />;
  }

  return (
    <div
      className="guest-intake-form"
      style={{
        backgroundColor: getBoxBackgroundColor(),
        boxSizing: 'border-box',
        maxWidth: 480,
        width: 'calc(100% - 32px)',
        margin: '16px auto',
        padding: '20px 16px',
        borderRadius: 8,
      }}
    >
      <div className="guest-intake-form__brand">
        <Logo variant="auth" style={{ marginTop: 10 }} type={getAuthLogoType()} />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            paddingTop: 12,
            margin: '0 auto',
          }}
        >
          <LanguageSelect color={getTextColor()} />
        </div>
      </div>

      <div ref={intakeFormRef} style={{ marginBottom: 16 }}>
        <Typography.Title heading={2} className="guest-intake-form__headline" style={{ marginTop: 12, textAlign: 'left' }}>
          {t('guest_intake.headline')}
        </Typography.Title>
        <Typography.Text
          className="guest-intake-form__subtext"
          style={{ display: 'block', marginTop: 12, marginBottom: 24, textAlign: 'left' }}
        >
          {t('guest_intake.subtext')}
        </Typography.Text>

      <Form form={form} layout="vertical" size="default" onSubmit={onSubmit} scrollToFirstError>
        <FormItem
          label={t('register_page.name_label')}
          field="name"
          rules={[requiredTrimRule(t('register_page.name_placeholder'))]}
        >
          <Input placeholder={t('register_page.name_placeholder')} autoComplete="off" />
        </FormItem>

        <FormItem
          label={t('register_page.last_name_label')}
          field="last_name"
          rules={[requiredTrimRule(t('register_page.last_name_placeholder'))]}
        >
          <Input placeholder={t('register_page.last_name_placeholder')} autoComplete="off" />
        </FormItem>

        <FormItem
          label={t('profile.short_description_label')}
          field="shortDescription"
          rules={[requiredTrimRule(t('profile.short_description_placeholder'))]}
        >
          <Input placeholder={t('profile.short_description_placeholder')} />
        </FormItem>

        <FormItem
          label={t('profile.bio_label')}
          field="fullBio"
          rules={[requiredTrimRule(t('profile.bio_placeholder'))]}
        >
          <Input.TextArea placeholder={t('profile.bio_placeholder')} rows={4} />
        </FormItem>

        <FormItem
          label={t('profile.language_label')}
          field="language"
          rules={[requiredTrimRule(t('profile.language_placeholder'))]}
        >
          <Select
            placeholder={t('profile.language_placeholder')}
            options={[
              { label: t('common.english'), value: 'en' },
              { label: t('common.spanish'), value: 'es' },
              { label: t('guest_intake.language_bilingual_option'), value: 'both' },
            ]}
          />
        </FormItem>

        <Typography.Text type="secondary" className="guest-intake-form__notice" style={{ display: 'block', marginBottom: 16, textAlign: 'left' }}>
          {t('guest_intake.locked_fields_notice')}
        </Typography.Text>

        <Button
          htmlType="submit"
          loading={submitting}
          size="large"
          shape="round"
          long
          style={{ color: getButtonTextColor(), backgroundColor: getButtonColor() }}
        >
          {isEditing ? t('guest_intake.save_button') : t('guest_intake.confirm_button')}
        </Button>
      </Form>
      </div>

      <div
        ref={roleChoiceRef}
        className={highlightRoleChoice ? 'guest-intake-form__role-choice guest-intake-form__role-choice--highlight' : 'guest-intake-form__role-choice'}
      >
        <GuestRoleChoicePanel
          id={GUEST_ROLE_CHOICE_ID}
          expired={Boolean(user?.guestExpired)}
          submitting={Boolean(convertingRole)}
          submittingRole={convertingRole}
          onSelectRole={(role) => void convertRole(role)}
          align="left"
        />
      </div>
    </div>
  );
};
